import React, { useCallback } from 'react';
import * as MD from '@material-ui/core';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { SvgIcon } from '@material-ui/core';
import FlagIconFactory from 'react-flag-icon-css';
import { withTheme } from '@material-ui/styles';
import { Dispatch } from 'redux';
import { IApplicationState, IConnectedReduxProps } from '../Store';
import { Locales } from '../Store/I18n/Constants';
import { I18N, ILocaleInfos } from '../Store/I18n/Types';
import { Config } from '../config';
import { TypedStorage } from '../Utils/TypedStorage';
import { AdtrackLogoIcon } from '../Themes/svg/adtrack-logo';
import { ILayoutState } from '../Store/Layout/Types';
import { IUserState } from '../Store/User/Types';
// ACTIONS
import { setLocale } from '../Store/I18n/Actions';
import { hideDrawer, showDrawer, toggleTheme } from '../Store/Layout/Actions';
import { logout } from '../Store/User/Actions';
// ICONS
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import AssignmentIcon from '@material-ui/icons/Assignment';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SearchIcon from '@material-ui/icons/Search';
import ToggleOnIcon from '@material-ui/icons/ToggleOn';
import ToggleOffIcon from '@material-ui/icons/ToggleOff';
import LinkIcon from '@material-ui/icons/Link';
import { PaletteMode } from '../Themes';
import { ISearchState } from '../Store/Search/Types';
import { withStyles, WithStyles } from '@material-ui/styles';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import styles from './AppBar.style';

const FlagIcon = FlagIconFactory(React, { useCssModules: false });

interface IOwnState {
  localeMenuAnchor: HTMLElement | null;
  localeMenuOpen: boolean;
  userMenuAnchor: HTMLElement | null;
  userMenuOpen: boolean;
  productMenuAnchor: HTMLElement | null;
  productMenuOpen: boolean;
}

interface IProps {
  hideDrawerIcon?: boolean;
}

interface IPropsFromState {
  i18n: I18N;
  layout: ILayoutState;
  localeInfos: ILocaleInfos;
  search: ISearchState;
  router: RouteComponentProps<{}>;
  user: IUserState;
  width: string;
}

interface IPropsFromDispatch {
  hideDrawer: typeof hideDrawer;
  logout: typeof logout;
  setLocale: typeof setLocale;
  showDrawer: typeof showDrawer;
  toggleTheme: typeof toggleTheme;
}

interface IPropsTheme {
  theme: any;
}

type AllProps = WithStyles<typeof styles> &
  IPropsFromState &
  IPropsFromDispatch &
  RouteComponentProps<{}> &
  IPropsTheme &
  IConnectedReduxProps &
  IProps;

const initialState = {
  localeMenuAnchor: null as HTMLElement | null,
  localeMenuOpen: false,
  userMenuAnchor: null as HTMLElement | null,
  userMenuOpen: false,
  productMenuAnchor: null as HTMLElement | null,
  productMenuOpen: false,
};

const AppBarComponent: React.FC<AllProps> = (props) => {
  const {
    classes,
    layout,
    user,
    i18n,
    router,
    logout,
    setLocale,
    toggleTheme,
  } = props;
  const [state, setState] = React.useState<IOwnState>(initialState);
  const theme = useTheme();
  const isXs = layout.showDrawer
    ? useMediaQuery(theme.breakpoints.down('sm'))
    : useMediaQuery(theme.breakpoints.down('xs'));
  const isSm = layout.showDrawer
    ? useMediaQuery(theme.breakpoints.down('md'))
    : useMediaQuery(theme.breakpoints.down('sm'));

  // Memoize event handlers to prevent unnecessary re-renders
  const handleEvent = useCallback(
    (ev: React.MouseEvent<HTMLElement>, stateUpdate: Partial<IOwnState>) => {
      ev.preventDefault();
      ev.stopPropagation();
      setState((prevState) => ({ ...prevState, ...stateUpdate }));
    },
    []
  );

  const handleUserMenuOpen = useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      handleEvent(ev, { userMenuAnchor: ev.currentTarget, userMenuOpen: true });
    },
    [handleEvent]
  );

  const handleLocaleMenuOpen = useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      handleEvent(ev, {
        localeMenuAnchor: ev.currentTarget,
        localeMenuOpen: true,
      });
    },
    [handleEvent]
  );

  const handleMenuClose = useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      handleEvent(ev, initialState);
    },
    [handleEvent]
  );

  const handleProductMenuOpen = useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      handleEvent(ev, {
        productMenuAnchor: ev.currentTarget,
        productMenuOpen: true,
      });
    },
    [handleEvent]
  );

  const handleLogout = useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      handleEvent(ev, initialState);
      logout();
    },
    [handleEvent, logout]
  );

  const handleUpdatePwd = useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      ev.preventDefault();
      ev.stopPropagation();
      let token = TypedStorage.get('userToken', '');
      let user_id = user.userInfo.id;
      window.open(
        `${Config.UsersUrl}/new_password/?token=${token}&id=${user_id}`
      );
    },
    [user.userInfo.id]
  );

  const onMenuTabChange = useCallback(
    (ev: React.MouseEvent<HTMLElement>, value: string) => {
      ev.preventDefault();
      ev.stopPropagation();
      if (ev.target === null) {
        return;
      }
      router.history.push(value);
    },
    [router.history]
  );

  const toggleDrawer = useCallback(() => {
    if (layout.showDrawer) {
      props.hideDrawer();
      return;
    }
    props.showDrawer();
  }, [layout.showDrawer, props.hideDrawer, props.showDrawer]);

  const onLocaleChange = useCallback(
    (locale: Locales) => {
      setState({ ...initialState });
      setLocale(locale);
    },
    [setLocale]
  );

  const getTabLabel = useCallback(
    (isXs: boolean, icon: React.ReactNode, text: string) => {
      return isXs ? (
        icon
      ) : (
        <MD.Grid alignItems="center" container>
          {text}
        </MD.Grid>
      );
    },
    []
  );

  let currentPath = '/';
  const pathParts = router.location.pathname.split('/');
  let logoColor = '#FFFFFF';
  if (theme.palette.type === PaletteMode.LIGHT) {
    logoColor = theme.palette.text.primary;
  }
  switch (pathParts.length > 1 ? pathParts[1] : '') {
    case '':
    case 'search':
    case 'details':
    case 'details-television':
    case 'details-radio':
      currentPath = '/';
      break;
    case 'declarations':
      currentPath = `/${pathParts[1]}`;
      break;
    default:
      currentPath = 'none';
  }

  return (
    <MD.AppBar
      className={layout.showDrawer ? classes.appBar : classes.appBarFull}
    >
      <MD.Toolbar className={classes.toolbar}>
        {!props.hideDrawerIcon && (
          <MD.IconButton onClick={toggleDrawer}>
            <MD.Icon color="primary">
              <MoreVertIcon />
            </MD.Icon>
          </MD.IconButton>
        )}
        <MD.Box
          className={classes.logoStyle}
          aria-haspopup="true"
          color="secondary"
          onClick={handleProductMenuOpen}
        >
          <SvgIcon component={AdtrackLogoIcon} htmlColor={logoColor} />
        </MD.Box>
        <MD.Tabs
          className={classes.tabs}
          onChange={onMenuTabChange as any}
          indicatorColor="primary"
          TabIndicatorProps={{ className: classes.tabsIndicator }}
          textColor="primary"
          value={currentPath}
        >
          <MD.Tab
            className={classes.tabItem}
            label={getTabLabel(
              layout.showDrawer ? isSm : isXs,
              <SearchIcon />,
              i18n._('Search')
            )}
            value={'/'}
          />
          <MD.Tab
            className={classes.tabItem}
            label={getTabLabel(
              layout.showDrawer ? isSm : isXs,
              <AssignmentIcon />,
              i18n._('My declarations')
            )}
            value={'/declarations'}
          />
          <MD.Tab
            className={classes.tabItem}
            label={getTabLabel(
              layout.showDrawer ? isSm : isXs,
              <LinkIcon />,
              i18n._('Catalog Manager')
            )}
            onClick={() => window.open(Config.catalogURL, '_blank')}
          />
        </MD.Tabs>
        <div id="user-menu" className={classes.userMenuContainer}>
          <MD.IconButton
            aria-haspopup="true"
            className={classes.profileStyle}
            onClick={handleUserMenuOpen}
          >
            <AccountCircleIcon />
          </MD.IconButton>
          <MD.IconButton
            className={classes.language}
            aria-haspopup="true"
            onClick={handleLocaleMenuOpen}
            size="small"
          >
            <FlagIcon code={props.localeInfos.iconName} />
          </MD.IconButton>
          <MD.Menu
            anchorEl={state.userMenuAnchor}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'top',
            }}
            className={classes.menus}
            id="menu-user"
            transformOrigin={{
              horizontal: 'left',
              vertical: 'bottom',
            }}
            onClose={handleMenuClose as any}
            open={state.userMenuOpen}
          >
            <MD.Button
              className={classes.menuItems}
              component="a"
              title={i18n._('My account')}
              variant="text"
            >
              {user.username}
            </MD.Button>
            <MD.Button
              className={classes.menuItems}
              component="a"
              onClick={handleLogout as any}
              title={i18n._('Log out my session')}
              variant="text"
            >
              {i18n._('Logout')}
            </MD.Button>
            <MD.Button
              className={classes.menuItems}
              component="a"
              onClick={handleUpdatePwd as any}
              title={i18n._('Update my password')}
              variant="text"
            >
              {i18n._('Update my password')}
            </MD.Button>
          </MD.Menu>
          <MD.Menu
            anchorEl={state.localeMenuAnchor}
            className={classes.menus}
            id="menu-locale"
            onClose={handleMenuClose as any}
            open={state.localeMenuOpen}
          >
            <MD.Button
              className={classes.menuItems}
              onClick={() => onLocaleChange(Locales.French)}
              variant="text"
            >
              <FlagIcon code={'fr'} />
              <span style={{ paddingLeft: 8 }}>
                {i18n._('Switch to French')}
              </span>
            </MD.Button>
            <MD.Button
              className={classes.menuItems}
              onClick={() => onLocaleChange(Locales.English)}
              variant="text"
            >
              <FlagIcon code={'gb'} />
              <span style={{ paddingLeft: 8 }}>
                {i18n._('Switch to English')}
              </span>
            </MD.Button>
          </MD.Menu>
          <MD.IconButton
            style={{ margin: 0, padding: 0 }}
            onClick={toggleTheme}
          >
            {layout.theme === PaletteMode.DARK ? (
              <ToggleOnIcon color="secondary" />
            ) : (
              <ToggleOffIcon />
            )}
          </MD.IconButton>
        </div>
      </MD.Toolbar>
    </MD.AppBar>
  );
};

const mapStateToProps = (
  { i18n, layout, user, search }: IApplicationState,
  ownProps: RouteComponentProps<{}>
) => ({
  i18n: i18n.i18n,
  layout,
  localeInfos: i18n.localeInfos,
  router: ownProps,
  search,
  user,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  hideDrawer: () => dispatch(hideDrawer()),
  logout: () => dispatch(logout()),
  setLocale: (locale: Locales) => dispatch(setLocale(locale)),
  showDrawer: () => dispatch(showDrawer()),
  toggleTheme: () => dispatch(toggleTheme()),
});

export const AppBar = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTheme(withStyles(styles)(React.memo(AppBarComponent))));
